import { IChartData } from "../interfaces";
import { ITableData, ITableItem } from "../components/table/interfaces";

const sortChartItemsByDate = <T extends { date: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

export const mergeRecorderModeChartData = (base: IChartData[], incoming: IChartData[]): IChartData[] => {
  const map = new Map<string, IChartData>();
  const keyOf = (s: IChartData) => `${s.idPoint ?? ""}|${s.name ?? ""}|${s.parameterName ?? ""}`;

  for (const s of base) {
    map.set(keyOf(s), {
      ...s,
      items: [...s.items],
    });
  }

  for (const s of incoming) {
    const key = keyOf(s);
    const existing = map.get(key);

    if (!existing) {
      map.set(key, {
        ...s,
        items: [...s.items],
      });
      continue;
    }

    const existingDates = new Set(existing.items.map(it => it.date));
    for (const it of s.items) {
      if (!existingDates.has(it.date)) {
        existing.items.push(it);
        existingDates.add(it.date);
      }
    }
  }

  return Array.from(map.values()).map(s => ({
    ...s,
    items: sortChartItemsByDate(s.items),
  }));
};

export const mergeRecorderModeTableData = (base: ITableData, incoming: ITableData): ITableData => {
  if (!base?.rows?.length) {
    return {
      type: incoming?.type ?? base?.type,
      rows: incoming?.rows ? [...incoming.rows] : [],
      items: incoming?.items ? incoming.items.map(it => ({ ...it, values: [...it.values] })) : [],
    } as ITableData;
  }

  const finalRows = [...base.rows];
  for (const r of incoming?.rows ?? []) {
    if (!finalRows.includes(r)) finalRows.push(r);
  }

  const map = new Map<string, ITableItem>();

  for (const it of base.items ?? []) {
    map.set(it.name, {
      ...it,
      values: [...it.values, ...new Array(finalRows.length - base.rows.length).fill(null)],
    });
  }

  for (const it of incoming.items ?? []) {
    const existing = map.get(it.name);
    if (!existing) {
      const values = new Array(finalRows.length).fill(null);
      for (let j = 0; j < (incoming.rows ?? []).length; j++) {
        const row = incoming.rows[j];
        const idx = finalRows.indexOf(row);
        if (idx !== -1) values[idx] = it.values[j];
      }
      map.set(it.name, { ...it, values });
      continue;
    }

    for (let j = 0; j < (incoming.rows ?? []).length; j++) {
      const row = incoming.rows[j];
      const idx = finalRows.indexOf(row);
      if (idx === -1) continue;
      if (existing.values[idx] == null) existing.values[idx] = it.values[j];
    }
  }

  const resultItems: ITableItem[] = [];
  const seen = new Set<string>();
  for (const it of base.items ?? []) {
    const m = map.get(it.name);
    if (m) {
      resultItems.push(m);
      seen.add(it.name);
    }
  }
  for (const it of incoming.items ?? []) {
    if (!seen.has(it.name)) {
      const m = map.get(it.name);
      if (m) resultItems.push(m);
    }
  }

  return {
    type: base.type ?? incoming.type,
    rows: finalRows,
    items: resultItems,
  } as ITableData;
};

export const mergeRecorderModeTableDataMultiple = (
  base: ITableData | null,
  incoming: ITableData[],
): ITableData | null => {
  if (!incoming?.length) return base ?? null;
  if (!base) {
    return incoming.length === 1
      ? incoming[0]
      : incoming.slice(1).reduce((acc, t) => mergeRecorderModeTableData(acc, t), incoming[0]);
  }
  return incoming.reduce((acc, t) => mergeRecorderModeTableData(acc, t), base);
};
